import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { type Project, listProjects } from "../api/crm.ts";

const STATUS_COLORS: Record<string, string> = {
  PLANNING: "text-gray-300 bg-gray-700",
  REQUIREMENTS: "text-blue-400 bg-blue-900/30",
  ARCHITECTURE: "text-purple-400 bg-purple-900/30",
  DEVELOPMENT: "text-yellow-400 bg-yellow-900/30",
  TESTING: "text-orange-400 bg-orange-900/30",
  STAGING: "text-cyan-400 bg-cyan-900/30",
  READY_FOR_RELEASE: "text-teal-400 bg-teal-900/30",
  PRODUCTION: "text-green-400 bg-green-900/30",
  MAINTENANCE: "text-indigo-400 bg-indigo-900/30",
  COMPLETED: "text-emerald-400 bg-emerald-900/30",
  BLOCKED: "text-red-400 bg-red-900/30",
  CANCELLED: "text-gray-500 bg-gray-800",
};

export function ProjectsPage() {
  const { token } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    listProjects(token, { search: search || undefined, status: status || undefined, limit, offset: page * limit })
      .then((res) => { setProjects(res.items); setTotal(res.total); })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load projects"))
      .finally(() => setLoading(false));
  }, [token, search, status, page]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Projects</h1>
          <span className="text-sm text-gray-500">{total} total</span>
        </div>

        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Search projects…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(0); }}
            className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
          >
            <option value="">All statuses</option>
            {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}
        {!loading && projects.length === 0 && <p className="text-gray-500">No projects found.</p>}

        <div className="space-y-2">
          {projects.map((p) => (
            <div key={p.id} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{p.name}</p>
                  {p.description && <p className="text-sm text-gray-400 mt-0.5 truncate">{p.description}</p>}
                  <div className="flex gap-3 mt-1 text-xs text-gray-500">
                    {p.budget != null && <span>${p.budget.toLocaleString()} {p.currency}</span>}
                    {p.startDate && <span>starts {new Date(p.startDate).toLocaleDateString()}</span>}
                    {p.endDate && <span>due {new Date(p.endDate).toLocaleDateString()}</span>}
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded font-medium shrink-0 ${STATUS_COLORS[p.status] ?? "text-gray-400 bg-gray-800"}`}>
                  {p.status}
                </span>
              </div>
            </div>
          ))}
        </div>

        {total > limit && (
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1 rounded bg-gray-800 text-sm text-gray-300 disabled:opacity-40">Previous</button>
            <span className="text-sm text-gray-500 self-center">{page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}</span>
            <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * limit >= total} className="px-3 py-1 rounded bg-gray-800 text-sm text-gray-300 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </Layout>
  );
}
